import { ReactNode, useCallback } from "react";

import ListBoxItem from "ds/components/ListBox/Item";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";

import styles from "./styles.module.css";
import { BaseSelectItem } from "./types";

type SelectItemProps = {
  id: string;
  label: string;
  value: string;
  suffix?: ReactNode;
  multiple?: boolean;
  switchToggle?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (value: string, option?: BaseSelectItem) => void;
  closeSelect?: () => void;
};

const getTickboxLabel = (isSwitch: boolean, isChecked: boolean) => {
  if (isSwitch) {
    return isChecked ? "Switch off" : "Switch on";
  }

  return isChecked ? "Unselect" : "Select";
};

const SelectItem = ({
  id,
  label,
  value,
  suffix,
  multiple,
  switchToggle,
  checked,
  disabled,
  onChange,
  closeSelect,
}: SelectItemProps) => {
  const handleOnChange = useCallback(
    (e?: MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (disabled) return false;

      onChange?.(value, {
        value,
        label,
      });

      if (!multiple) {
        closeSelect?.();
      }

      return undefined;
    },
    [closeSelect, disabled, label, multiple, onChange, value]
  );

  return (
    <ListBoxItem id={id} textValue={label}>
      {multiple && (
        <Toggle
          variant={switchToggle ? "switch" : "checkbox"}
          id={id}
          onChange={() => handleOnChange()}
          isSelected={checked}
          className={styles.tickbox}
          isDisabled={disabled}
          aria-label={getTickboxLabel(!!switchToggle, !!checked)}
        />
      )}
      <TextEllipsis tooltip={label} tooltipWidthMode="maxWidthSm">
        {(props) => (
          <Typography tag="span" variant="p-body3" color="primary" {...props}>
            {label}
          </Typography>
        )}
      </TextEllipsis>
      {suffix}
    </ListBoxItem>
  );
};

SelectItem.displayName = "DS.Select.Item";

export default SelectItem;
